import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { LoginOtpService } from 'src/app/_services/login-otp.service';
import { SnackbarService } from 'src/app/_services/snackBar.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackBarErrorComponent } from 'src/app/components/snack-bar-error/snack-bar-error.component';

@Component({
  selector: 'app-login-otp-modal',
  templateUrl: './login-otp-modal.component.html',
  styleUrls: ['./login-otp-modal.component.scss']
})
export class LoginOtpModalComponent {
  @Input() email: string;
  @Input() userId: number;
  // otp_type returned by the login response , e.g. 'first_time_login' or 'internal_staff_login'
  @Input() otpType: string;

  @Output() verified = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();

  otp: string;
  isOtpEntered = false;
  loading = false;
  showError = false;
  errorMsg = '';
  shakeOtp = false;

  pauseResending = false;
  timeLeft: number = 60;
  formattedTime: string = '01:00';
  private timerInterval: any;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private loginOtpService: LoginOtpService,
    private snackBar: SnackbarService,
    private _snackBar: MatSnackBar
  ) {}

  onOtpEntered(otp: string) {
    this.showError = false;
    if (otp?.length === 6 && this.isValidOTP(otp)) {
      this.otp = this.sanitizeOTPAsEnglishNumsForBackend(otp);
      this.isOtpEntered = true;
    } else {
      this.isOtpEntered = false;
    }
  }

  // keeps the old behaviour when the caller doesn't pass an otp type
  get type(): string {
    return this.otpType ? this.otpType : 'first_time_login';
  }

  verifyOtp() {
    if (!this.isOtpEntered) {
      this.triggerShakeEffect();
      return;
    }
    this.loading = true;
    this.loginOtpService
      .verifyLoginOtp({ user_id: this.userId as any, otp_code: this.otp as any, type: this.type } as any)
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.close();
          this.verified.emit(res);
        },
        error: (err) => {
          this.loading = false;
          const errorCode = err?.error?.error_type;
          // Show inline error for INVALID_OTP and OTP_EXPIRED
          if (errorCode === 'INVALID_OTP' || errorCode === 'OTP_EXPIRED') {
            this.otpInputError(err?.error?.message);
          } else {
            // Show system error popup for any other error (server down, etc.)
            const errorMsg = err?.error?.message;
            this.snackBar.setSnackBarMessage(errorMsg);
            this._snackBar.openFromComponent(SnackBarErrorComponent, {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            });
          }
        }
      });
  }

  resendOtp() {
    this.pauseResending = true;
    this.startTimer();
    this.loginOtpService
      .resendLoginOtp({ user_id: this.userId as any, type: this.type, channel: 'email' } as any)
      .subscribe({
        error: (err) => {
          const errorMsg = err?.error?.message;
          this.snackBar.setSnackBarMessage(errorMsg);
          this._snackBar.openFromComponent(SnackBarErrorComponent, {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        }
      });
  }

  triggerShakeEffect() {
    this.shakeOtp = true;
    setTimeout(() => (this.shakeOtp = false), 400);
  }

  otpInputError(msg: string) {
    this.errorMsg = msg;
    this.showError = true;
  }

  startTimer() {
    this.pauseResending = true;
    this.timeLeft = 60;
    this.updateFormattedTime();
    this.clearTimer();
    this.timerInterval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
        this.updateFormattedTime();
      } else {
        this.clearTimer();
        this.pauseResending = false;
      }
    }, 1000);
  }

  updateFormattedTime() {
    const minutes = Math.floor(this.timeLeft / 60).toString().padStart(2, '0');
    const seconds = (this.timeLeft % 60).toString().padStart(2, '0');
    const formatted = `${minutes}:${seconds}`;
    this.formattedTime = localStorage.getItem('lang') == 'ar' ? this.toArabicNumerals(formatted) : formatted;
  }

  toArabicNumerals(num: string): string {
    return num.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]);
  }

  clearTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  isValidOTP(otp: string | number): boolean {
    const otpStr = otp.toString().trim();
    const otpRegex = /^[0-9٠-٩]+$/;
    return otpRegex.test(otpStr);
  }

  sanitizeOTPAsEnglishNumsForBackend(otp: string | number): string {
    let otpStr = otp.toString().trim();
    const arabicToEnglishMap: { [key: string]: string } = {
      '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
      '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9'
    };
    otpStr = otpStr.replace(/[٠-٩]/g, (digit) => arabicToEnglishMap[digit]);
    return otpStr;
  }

  close() {
    this.clearTimer();
    this.showError = false;
    this.shakeOtp = false;
  }

  onCancel() {
    this.close();
    this.cancel.emit();
  }
}


